import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Mail, Phone, Trash2, Inbox, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";

const AdminDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/contacts");
      setContacts(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const markRead = async (c) => {
    const next = c.status === "new" ? "read" : "new";
    try {
      await api.patch(`/admin/contacts/${c.id}`, { status: next });
      setContacts((prev) => prev.map((x) => (x.id === c.id ? { ...x, status: next } : x)));
    } catch {
      toast.error("Could not update status.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/contacts/${id}`);
      setContacts((prev) => prev.filter((x) => x.id !== id));
      toast.success("Submission deleted.");
    } catch {
      toast.error("Could not delete submission.");
    }
  };

  const newCount = contacts.filter((c) => c.status === "new").length;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="bg-navy">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
          <Logo light className="text-lg" />
          <div className="flex items-center gap-4">
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="text-slate-300 hover:text-white transition-colors p-2"
              title="Refresh"
            >
              <RefreshCw size={18} />
            </button>
            <button
              data-testid="admin-logout"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              <LogOut size={16} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-2xl sm:text-3xl font-medium text-navy">Consultation Inquiries</h1>
            <p className="mt-1 text-sm text-slate-500">
              {contacts.length} total · {newCount} new
            </p>
          </div>
        </div>

        {loading ? (
          <p className="text-slate-500">Loading…</p>
        ) : contacts.length === 0 ? (
          <div className="border border-slate-200 bg-white p-16 text-center">
            <Inbox size={40} className="text-slate-300 mx-auto" />
            <p className="mt-4 text-slate-500">No inquiries yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {contacts.map((c) => (
              <div
                key={c.id}
                data-testid={`contact-row-${c.id}`}
                className={`border bg-white p-6 ${c.status === "new" ? "border-gold/60" : "border-slate-200"}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-medium text-navy">{c.full_name}</h3>
                      {c.status === "new" && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider bg-gold text-white px-2 py-0.5">New</span>
                      )}
                    </div>
                    {c.company_name && <p className="text-sm text-slate-500">{c.company_name}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`mark-read-${c.id}`}
                      onClick={() => markRead(c)}
                      className="text-xs border border-slate-300 px-3 py-1.5 text-slate-600 hover:border-gold hover:text-gold transition-colors"
                    >
                      {c.status === "new" ? "Mark read" : "Mark new"}
                    </button>
                    <button
                      data-testid={`delete-${c.id}`}
                      onClick={() => remove(c.id)}
                      className="text-slate-400 hover:text-red-600 transition-colors p-1.5"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 text-slate-600 hover:text-gold">
                    <Mail size={14} /> {c.email}
                  </a>
                  {c.phone && (
                    <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 text-slate-600 hover:text-gold">
                      <Phone size={14} /> {c.phone}
                    </a>
                  )}
                </div>

                {c.service_of_interest && (
                  <p className="mt-3 text-sm">
                    <span className="text-slate-400">Service: </span>
                    <span className="text-navy font-medium">{c.service_of_interest}</span>
                  </p>
                )}
                <p className="mt-3 text-sm leading-relaxed text-slate-600 whitespace-pre-wrap">{c.message}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className={`text-[11px] px-2 py-0.5 border ${c.sms_transactional_consent ? "border-sage text-sage" : "border-slate-200 text-slate-400"}`}>
                    {c.sms_transactional_consent ? "✓" : "✕"} Transactional SMS
                  </span>
                </div>
                <p className="mt-3 text-xs text-slate-400">
                  {new Date(c.created_at).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
